import type { CSSProperties } from "react";

/**
 * Novala integration logo component.
 *
 * Renders the official brand mark for a third-party service that
 * Novala integrates with (Stripe, Shopify, PayPal, Amazon, etc.).
 *
 * Logo source resolution:
 *   1. If the brand is in Simple Icons' catalog → use Simple Icons CDN
 *      with the brand's primary color override.
 *   2. If the brand is NOT in Simple Icons (Lightspeed, Chargebee,
 *      Recurly, etc.) → use Clearbit's logo API with the brand's
 *      public domain. Clearbit serves the real logo from the company's
 *      website.
 *
 * NO letter-mark fallbacks. If a brand ends up here that neither
 * source has, the broken image is a flag to source its SVG manually.
 *
 * Production (follow-up PR): swap both CDNs for locally imported SVGs
 * from src/assets/brands/, downloaded from each brand's press kit.
 * This guarantees offline support, no CDN dependency, and compliance
 * with each brand's trademark guidelines.
 */

export type IntegrationBrandId =
  | "stripe"
  | "paypal"
  | "square"
  | "adyen"
  | "braintree"
  | "mollie"
  | "razorpay"
  | "shopify"
  | "woocommerce"
  | "bigcommerce"
  | "wix"
  | "squarespace"
  | "magento"
  | "amazon"
  | "etsy"
  | "ebay"
  | "walmart"
  | "mercadopago"
  | "clover"
  | "lightspeed"
  | "chargebee"
  | "recurly";

interface IntegrationLogoProps {
  brandId: IntegrationBrandId | string;
  name: string;
  size?: number;
}

/**
 * Brand color overrides for Simple Icons. Simple Icons defaults to a
 * grey mark; the override renders the icon in the brand's primary
 * color (hex without the # prefix per Simple Icons URL convention).
 */
const SIMPLE_ICONS_COLORS: Record<string, string> = {
  stripe: "635BFF",
  paypal: "003087",
  square: "000000",
  adyen: "0ABF53",
  braintree: "00457C",
  mollie: "000000",
  razorpay: "072654",
  shopify: "96BF48",
  woocommerce: "96588A",
  bigcommerce: "121117",
  wix: "0C6EFC",
  squarespace: "000000",
  magento: "EB7203",
  amazon: "FF9900",
  etsy: "F56400",
  ebay: "E53238",
  walmart: "0071DC",
  mercadopago: "00B1EA",
  clover: "00A862",
};

/**
 * Brands not in Simple Icons: map to the company's public domain so
 * Clearbit's logo API can serve the real brand logo. When these are
 * added to Simple Icons (or we download the official SVG into
 * src/assets/brands/), move them to SIMPLE_ICONS_COLORS or the local
 * registry respectively.
 */
const CLEARBIT_DOMAINS: Record<string, string> = {
  lightspeed: "lightspeedhq.com",
  chargebee: "chargebee.com",
  recurly: "recurly.com",
};

const SIMPLE_ICONS_BASE = "https://cdn.simpleicons.org";
const CLEARBIT_BASE = "https://logo.clearbit.com";

function brandLogoUrl(brandId: string): string {
  if (brandId in CLEARBIT_DOMAINS) {
    return `${CLEARBIT_BASE}/${CLEARBIT_DOMAINS[brandId]}`;
  }
  const color = SIMPLE_ICONS_COLORS[brandId];
  return color
    ? `${SIMPLE_ICONS_BASE}/${brandId}/${color}`
    : `${SIMPLE_ICONS_BASE}/${brandId}`;
}

export function IntegrationLogo({
  brandId,
  name,
  size = 48,
}: IntegrationLogoProps) {
  const wrapStyle: CSSProperties = {
    width: size,
    height: size,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  const imgStyle: CSSProperties = {
    maxWidth: "100%",
    maxHeight: "100%",
    display: "block",
    userSelect: "none",
    objectFit: "contain",
  };

  return (
    <div style={wrapStyle}>
      <img
        src={brandLogoUrl(brandId)}
        alt={`${name} logo`}
        style={imgStyle}
        loading="lazy"
      />
    </div>
  );
}